import { useEffect, useMemo } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { buildAntdTheme } from '@dtable/ui-tokens';
import { queryClient } from './lib/query-client';
import { AppRouter } from './routes';
import { useAuthStore } from './stores/auth-store';
import { useUiStore } from './stores/ui-store';
import { applyTenantBrand } from './lib/tenant';
import { InstallPrompt } from './components/InstallPrompt';

/**
 * Per-tenant brand application (§6.6 SaaS mode).
 * When the auth store loads the current user's organization, apply its brand
 * overrides to the CSS variables at the root.
 */
function TenantBrand() {
  const me = useAuthStore((s) => s.me);
  useEffect(() => {
    const brand = (me?.organization as any)?.brand;
    if (brand && typeof brand === 'object') {
      applyTenantBrand(brand);
    }
  }, [me]);
  return null;
}

export function App() {
  // Global light/dark theme. Toggled from the top-bar button (see AppShell).
  // The `data-theme` attribute drives all `--db-*` CSS variables in tokens.css;
  // `buildAntdTheme(mode)` swaps in the AntD dark algorithm so component
  // internals (Modal, Dropdown, Popover portals) pick it up automatically.
  const theme = useUiStore((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
  }, [theme]);
  const antdConfig = useMemo(() => buildAntdTheme(theme), [theme]);

  return (
    <ConfigProvider theme={antdConfig}>
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <TenantBrand />
          <InstallPrompt />
          <AppRouter />
          {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-right" />}
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
